/**
 * Translating the inspector's ~2 400 labels WITHOUT touching a single schema file (feature 0051 lot 4).
 *
 * ## The decision
 *
 * A schema field already has a unique address: the section type it belongs to, plus its own `id`.
 * `hero` + `heading` is `sec.hero.heading` and nothing else. So the key is DERIVED, never declared —
 * which is what makes this lot tractable at all:
 *
 *  - **57 `schema.ts` files stay untouched.** They are module-level constants evaluated at import;
 *    turning each into a function would have rippled into the registry, the builder, the storefront
 *    renderer AND the backend's section-catalog generator.
 *  - **`packages/ui-react` gains nothing.** It is shared with the STOREFRONT, which must never carry
 *    the seller's dictionary — those are two different vocabularies for two different readers (§5.2).
 *  - **The English literal in the schema stays, as the floor.** A key we have not written yet renders
 *    exactly what the builder renders today. Same contract as the `ui-angular` seam in lot 3.
 *
 * ## Where it applies
 *
 * One seam: the inspector, where it already holds the section type and the schema together. Threading
 * a section type down through seventeen control components would have been the other way, and every
 * new control would have had to remember to thread it.
 */
import type { SettingField } from '@aquilixshop/ui-react';

import { builderLocale } from './locale';
import { DICTIONARIES } from './locales';

/** The phrase for a key, or `undefined` — never the key itself, so the caller's floor can win. */
function lookup(key: string): string | undefined {
  const phrase = DICTIONARIES[builderLocale()]?.[key];
  return typeof phrase === 'string' && phrase.length > 0 ? phrase : undefined;
}

/** A UI string of the builder's own chrome (not a schema label). Falls back to the text passed in. */
export function bt(key: string, floor: string): string {
  return lookup(key) ?? floor;
}

/**
 * `bt` with `{param}` slots. The floor carries the same slots, so an untranslated phrase still
 * interpolates. An absent param leaves the slot visible — the bug is in the CALL, and the call
 * site is what has to be found (same contract as the dashboard's `interpolate`).
 */
export function btp(
  key: string,
  floor: string,
  params: Readonly<Record<string, string | number>>,
): string {
  return (lookup(key) ?? floor).replace(/\{(\w+)\}/g, (whole, name: string) =>
    params[name] === undefined ? whole : String(params[name]),
  );
}

/**
 * Two stems, tried in order — and the second is what makes this lot finite.
 *
 * `sec.<type>.<id>` addresses one field in one section. But the schemas are written to a convention,
 * and the measurement says how strongly: across the 57 files, **925 of the 1 017 distinct field ids
 * carry exactly ONE label everywhere they appear** — `paddingTop` is "Top padding" in all nineteen
 * sections that have it. Those 925 shared entries cover **1 292 of the 1 901 fields, 68 %**.
 *
 * So a shared `field.<id>` answer is tried when no section-specific one exists. It removes ~370
 * duplicate translations, and it is the reason a new section usually arrives already translated.
 * The ~92 ids whose label genuinely differs between sections keep a `sec.` entry, which wins.
 */
function stemsFor(ownerType: string, fieldId: string, parentSection?: string): readonly string[] {
  return [
    // A block type is only unique WITHIN its section: `collage` and `video` both ship a block called
    // `video`, and its `description` means two different things in the two. `sec.<owner>.<id>` cannot
    // tell them apart, so a block passes its parent section and gets a stem that can. Tried first,
    // written only where a real clash exists — three fields today.
    ...(parentSection ? [`sec.${parentSection}.block.${ownerType}.${fieldId}`] : []),
    `sec.${ownerType}.${fieldId}`,
    `field.${fieldId}`,
  ];
}

/** The first stem that answers, else the floor written in the schema. */
function resolve(stems: readonly string[], suffix: string, floor: string): string {
  for (const stem of stems) {
    const hit = lookup(suffix ? `${stem}.${suffix}` : stem);
    if (hit !== undefined) return hit;
  }
  return floor;
}

/** A group heading is named by the group, not by a field — it is shared by every field in it. */
export function groupLabel(sectionType: string, group: string): string {
  return resolve([`sec.${sectionType}.group.${group}`, `group.${group}`], '', group);
}

/**
 * Return the fields with their seller-facing text swapped for the reader's language.
 *
 * Everything else on the field is copied through untouched — `id`, `type`, `default`, `visibleWhen`,
 * and crucially a select option's `value`. **A translated option value would silently stop matching
 * the stored setting**, which is the twelfth time this project has met that failure; only `label`
 * moves.
 */
export function translateFields(
  ownerType: string,
  fields: readonly SettingField[],
  parentSection?: string,
): SettingField[] {
  return fields.map((field) => {
    const stems = stemsFor(ownerType, field.id, parentSection);
    const next = {
      ...field,
      label: resolve(stems, '', field.label),
      ...(field.info !== undefined ? { info: resolve(stems, 'info', field.info) } : {}),
      // A placeholder is seller-facing text like a label — the one that shipped in FRENCH inside an
      // English schema (`hero.ctaLabel`) is why this line exists. URLs and icon names have no entry
      // and fall through to the schema literal, which reads the same in every language.
      ...('placeholder' in field && field.placeholder !== undefined
        ? { placeholder: resolve(stems, 'ph', field.placeholder) }
        : {}),
      // A mobile slider's "unset" sentence ("Automatique (75 % du bureau)") — seller-facing text like a
      // label, and the one string on a field that is neither label, info nor placeholder.
      ...('inheritLabel' in field && field.inheritLabel !== undefined
        ? { inheritLabel: resolve(stems, 'inherit', field.inheritLabel) }
        : {}),
    } as SettingField;
    if (next.type === 'select') {
      // A third, coarser stem for options only: `opt.<value>`. Half the 1 850 option labels are the
      // same handful of words — left / right / center, small / medium / large — repeated across
      // dozens of fields, and one entry serves all of them.
      next.options = next.options.map((o) => ({
        ...o,
        label: resolve(
          [...stems.map((st) => `${st}.opt.${o.value}`), `opt.${o.value}`],
          '',
          o.label,
        ),
      }));
    }
    return next;
  });
}
